import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import axios from '../utils/axiosConfig';
import config from '../config/environment';

interface UseApiQueryOptions<T> extends Omit<UseQueryOptions<T, Error, T>, 'queryFn'> {
  url: string;
  transform?: (data: any) => T;
}

export function useApiQuery<T = any>(options: UseApiQueryOptions<T>) {
  const { url, transform, ...queryOptions } = options;

  return useQuery({
    queryFn: async (): Promise<T> => {
      try {
        const response = await axios.get(url);
        const data = response.data;
        
        // Verificar si los datos son un array cuando se espera un array
        if (Array.isArray(data) || data === null || data === undefined) {
          return transform ? transform(data) : data;
        }
        
        // Si no es un array pero se espera un array, retornar array vacío
        if (transform) {
          return transform(data);
        }
        
        return data;
      } catch (error) {
        console.error(`Error fetching ${url}:`, error);
        throw error;
      }
    },
    retry: config.maxRetries,
    retryDelay: config.retryDelay,
    ...queryOptions,
  });
}

// Hook específico para arrays
export function useApiQueryArray<T = any>(options: Omit<UseApiQueryOptions<T[]>, 'transform'>) {
  return useApiQuery<T[]>({
    ...options,
    transform: (data: any) => {
      if (Array.isArray(data)) {
        return data;
      }
      console.error('Expected array but got:', typeof data);
      return [];
    },
  });
}
